import { MutationCache, QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";
import { errorMessage, isErrorHandled } from "./errors";

export { errorMessage, markErrorHandled, NETWORK_ERROR_MESSAGE } from "./errors";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /** The caller shows its own error UI (inline form error, ConfirmDialog); skip the global toast. */
      silent?: boolean;
    } & Record<string, unknown>;
  }
}

type Reporter = (message: string) => void;
let reporter: Reporter | null = null;

/** The app shell registers the toast here once ToastProvider is mounted; returns the unregister function. */
export function setMutationErrorReporter(fn: Reporter): () => void {
  reporter = fn;
  return () => {
    if (reporter === fn) reporter = null;
  };
}

/**
 * Every failed mutation reaches the user: without this, a mutation with no onError and a caller that awaits
 * mutateAsync without a catch just stops its spinner. Mutations with their own onError, or meta.silent, opt out;
 * the Toast stack collapses an identical message a caller also toasts from its own catch. The report waits one task
 * because the cache's onError runs before mutateAsync rejects: a caller's catch gets the chance to markErrorHandled.
 */
export function reportMutationError(
  err: unknown,
  mutation: { options: { onError?: unknown; meta?: { silent?: boolean } } },
) {
  if (mutation.options.onError !== undefined || mutation.options.meta?.silent) return;
  setTimeout(() => {
    if (!isErrorHandled(err)) reporter?.(errorMessage(err));
  }, 0);
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (err, _vars, _ctx, mutation) => reportMutationError(err, mutation),
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // Client errors will not fix themselves on retry; only retry transient failures.
        retry: (failureCount, error) =>
          failureCount < 2 && !(error instanceof ApiError && error.status >= 400 && error.status < 500),
        refetchOnWindowFocus: false,
      },
    },
  });
}

export const queryClient = createQueryClient();
